import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  User,
  CreditCard,
  Coins,
  Receipt,
  Tag,
  BookOpen,
  ListChecks,
  Lock,
  Palette,
  LogOut,
  Shield,
  ChevronRight,
  Sparkles,
  Menu,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type SettingsSectionId =
  | "account"
  | "subscription"
  | "credits"
  | "billing"
  | "smart-categories"
  | "smart-notes"
  | "field-presets"
  | "security"
  | "appearance"
  | "review";

export interface SettingsSectionMeta {
  id: SettingsSectionId;
  label: string;
  description: string;
  icon: typeof User;
  group: "account" | "billing" | "features" | "preferences" | "community";
}

export const SETTINGS_SECTIONS: SettingsSectionMeta[] = [
  { id: "account", label: "Account", description: "Profile, verification & access status", icon: User, group: "account" },
  { id: "subscription", label: "Subscription", description: "Manage your plan and renewal", icon: CreditCard, group: "billing" },
  { id: "credits", label: "Credits", description: "Balance, usage & top-ups", icon: Coins, group: "billing" },
  { id: "billing", label: "Billing", description: "Invoices, payment methods & company details", icon: Receipt, group: "billing" },
  { id: "smart-categories", label: "Smart Categories", description: "Auto-tag contacts with AI categories", icon: Tag, group: "features" },
  { id: "smart-notes", label: "Smart Notes", description: "AI-generated research summaries", icon: BookOpen, group: "features" },
  { id: "field-presets", label: "Field Presets", description: "Customize the fields you extract", icon: ListChecks, group: "features" },
  { id: "security", label: "Security", description: "Password & account safety", icon: Lock, group: "preferences" },
  { id: "appearance", label: "Appearance", description: "Theme & visual options", icon: Palette, group: "preferences" },
  { id: "review", label: "Your Review", description: "Share feedback and earn 15 credits", icon: Star, group: "community" },
];

const GROUP_LABELS: Record<SettingsSectionMeta["group"], string> = {
  account: "Account",
  billing: "Billing",
  features: "Features",
  preferences: "Preferences",
  community: "Community",
};

interface SettingsLayoutProps {
  active: SettingsSectionId;
  onChange: (id: SettingsSectionId) => void;
  isAdmin: boolean;
  children: ReactNode;
}

export function SettingsLayout({ active, onChange, isAdmin, children }: SettingsLayoutProps) {
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const { canUseSmartCategories, canUseSmartNotes, canUseFieldPresets } = useFeatureAccess();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Filter out gated sections that the user has no access to. Keeps the
  // sidebar clean — Settings.tsx still renders FeatureUpgradeCard fallback
  // if someone navigates by URL during a tier transition.
  const visibleSections = useMemo(
    () =>
      SETTINGS_SECTIONS.filter((s) => {
        if (s.id === "smart-categories") return canUseSmartCategories;
        if (s.id === "smart-notes") return canUseSmartNotes;
        if (s.id === "field-presets") return canUseFieldPresets;
        return true;
      }),
    [canUseSmartCategories, canUseSmartNotes, canUseFieldPresets]
  );

  const activeMeta = visibleSections.find((s) => s.id === active) ?? SETTINGS_SECTIONS.find((s) => s.id === active) ?? SETTINGS_SECTIONS[0];

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast({ title: "Signed out", description: "You have been logged out" });
      navigate("/auth", { replace: true });
    } catch {
      toast({ title: "Error signing out", description: "Please try again", variant: "destructive" });
    }
  };

  const handleSelect = (id: SettingsSectionId) => {
    onChange(id);
    setMobileOpen(false);
  };

  // Group sections for visual rhythm
  const grouped = useMemo(
    () =>
      visibleSections.reduce<Record<string, SettingsSectionMeta[]>>((acc, s) => {
        (acc[s.group] = acc[s.group] || []).push(s);
        return acc;
      }, {}),
    [visibleSections]
  );

  // Avatar initials from email
  const initials = (user?.email ?? "??")
    .split("@")[0]
    .split(/[._-]/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const planLabel = subscription
    ? subscription.tier === "welcome"
      ? "Welcome plan"
      : `${subscription.tier.charAt(0).toUpperCase()}${subscription.tier.slice(1)} plan`
    : "No plan";

  const SidebarContent = (
    <div className="flex flex-col h-full bg-card/40">
      {/* Profile header */}
      <div className="p-5 border-b border-border/60">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="h-11 w-11 rounded-full bg-gradient-to-br from-primary to-primary-glow text-primary-foreground flex items-center justify-center font-semibold text-sm shadow-fluent-2">
              {initials || <User className="h-5 w-5" />}
            </div>
            {subscription && subscription.tier !== "welcome" && (
              <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-success border-2 border-card" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-tight truncate">{user?.email?.split("@")[0] ?? "Guest"}</p>
            <p className="text-[11px] text-muted-foreground truncate">{user?.email ?? "Not signed in"}</p>
          </div>
        </div>
        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
          <Sparkles className="h-3 w-3" />
          {planLabel}
        </div>
      </div>

      {/* Nav */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-5">
          {(Object.keys(grouped) as Array<keyof typeof GROUP_LABELS>).map((group) => (
            <div key={group}>
              <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70">
                {GROUP_LABELS[group]}
              </p>
              <ul className="space-y-0.5">
                {grouped[group].map((s) => {
                  const isActive = s.id === active;
                  const Icon = s.icon;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => handleSelect(s.id)}
                        className={cn(
                          "group relative w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all text-left",
                          "hover:bg-muted/60",
                          isActive && "bg-primary/10 text-primary font-medium shadow-sm"
                        )}
                      >
                        {isActive && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 bg-primary rounded-r" />
                        )}
                        <Icon className={cn("h-4 w-4 shrink-0 transition-colors", isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground")} />
                        <span className="flex-1 truncate">{s.label}</span>
                        {isActive && <ChevronRight className="h-3.5 w-3.5 text-primary" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* Footer actions */}
      <div className="border-t border-border/60 p-3 space-y-1">
        {isAdmin && (
          <Button asChild variant="ghost" size="sm" className="w-full justify-start gap-2 h-9">
            <Link to="/admin">
              <Shield className="h-4 w-4 text-muted-foreground" />
              Admin dashboard
            </Link>
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSignOut}
          className="w-full justify-start gap-2 h-9 text-muted-foreground hover:text-foreground"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </Button>
      </div>
    </div>
  );

  const ActiveIcon = activeMeta.icon;

  return (
    <div className="flex flex-col lg:flex-row w-full">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[260px] shrink-0 border-r border-border/60 sticky top-16 h-[calc(100vh-4rem)]">
        {SidebarContent}
      </aside>

      {/* Mobile section switcher bar */}
      <div className="lg:hidden flex items-center gap-3 px-4 py-3 border-b border-border/60 sticky top-[57px] md:top-16 bg-background/85 backdrop-blur z-30 w-full">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0">
              <Menu className="h-4 w-4" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[280px]">
            {SidebarContent}
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <ActiveIcon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight truncate">{activeMeta.label}</p>
            <p className="text-[11px] text-muted-foreground truncate">{GROUP_LABELS[activeMeta.group]}</p>
          </div>
        </div>
      </div>

      {/* Content area */}
      <main className="flex-1 min-w-0 w-full">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10">
          {/* Section header (desktop) */}
          <header className="mb-7 hidden lg:block">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
              <span>Settings</span>
              <ChevronRight className="h-3 w-3" />
              <span>{GROUP_LABELS[activeMeta.group]}</span>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/15 text-primary flex items-center justify-center shrink-0">
                <ActiveIcon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{activeMeta.label}</h1>
                <p className="text-sm text-muted-foreground mt-1">{activeMeta.description}</p>
              </div>
            </div>
            <Separator className="mt-6" />
          </header>

          {/* Mobile section description */}
          <header className="mb-5 lg:hidden">
            <p className="text-sm text-muted-foreground">{activeMeta.description}</p>
            <Separator className="mt-4" />
          </header>

          {children}
        </div>
      </main>
    </div>
  );
}

/** Hook to sync active section with URL `?section=` */
export function useSettingsSection(defaultSection: SettingsSectionId = "account") {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get("section") as SettingsSectionId | null;
  const valid = SETTINGS_SECTIONS.some((s) => s.id === raw);
  const active: SettingsSectionId = valid ? (raw as SettingsSectionId) : defaultSection;

  const setActive = (id: SettingsSectionId) => {
    const next = new URLSearchParams(searchParams);
    if (id === defaultSection) next.delete("section");
    else next.set("section", id);
    setSearchParams(next, { replace: false });
  };

  // Scroll to top on section change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [active]);

  return [active, setActive] as const;
}
